import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api from "@/lib/api";
import { STATUS_LABEL, STATUS_BADGE, RISK_BADGE, LOAN_PRODUCTS } from "@/lib/loanProducts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ExportCsvButton from "@/components/ExportCsvButton";

const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const productTitle = (key) => LOAN_PRODUCTS.find((p) => p.key === key)?.title || key;

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    const url = filter === "all" ? "/applications" : `/applications?status_filter=${filter}`;
    api.get(url).then(({ data }) => setApps(data || []));
  }, [filter]);

  return (
    <DashboardLayout role="admin">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-ink sm:text-3xl">Applications</h1>
          <p className="mt-1 text-sm text-ink-soft">Review, score and decide on every loan application.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportCsvButton endpoint="applications.csv" filename="jrc_applications.csv" testId="apps-export-csv" />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44" data-testid="apps-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="under_review">Under review</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-[#E5E2DE] bg-white">
        <div className="hidden grid-cols-12 gap-4 border-b border-[#E5E2DE] bg-[#F9F6F0] px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-muted md:grid">
          <div className="col-span-3">Applicant</div>
          <div className="col-span-3">Product</div>
          <div className="col-span-2">Amount / Term</div>
          <div className="col-span-2">Risk</div>
          <div className="col-span-2">Status</div>
        </div>
        {apps.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-ink-soft">No applications match this filter.</div>
        ) : apps.map((a) => (
          <Link
            key={a.id}
            to={`/admin/applications/${a.id}`}
            data-testid={`admin-app-row-${a.id}`}
            className="grid grid-cols-1 gap-2 border-b border-[#E5E2DE] px-5 py-4 transition-colors last:border-b-0 hover:bg-[#F9F6F0] md:grid-cols-12 md:gap-4"
          >
            <div className="md:col-span-3">
              <div className="font-heading text-sm font-semibold text-ink">{a.applicant_name}</div>
              <div className="text-xs text-ink-muted">{a.applicant_email}</div>
            </div>
            <div className="text-sm text-ink md:col-span-3">{productTitle(a.product)}
              <div className="text-xs text-ink-muted">{a.customer_type.replace(/_/g, " ")}</div>
            </div>
            <div className="text-sm text-ink md:col-span-2">{fmt(a.amount)} <span className="text-ink-muted">· {a.term_months} mo</span></div>
            <div className="md:col-span-2">
              {a.risk_band && (
                <span className={`rounded-full border px-2 py-0.5 text-xs ${RISK_BADGE[a.risk_band]}`}>
                  {a.risk_band} ({a.risk_score})
                </span>
              )}
            </div>
            <div className="md:col-span-2">
              <span className={`rounded-full border px-2 py-0.5 text-xs ${STATUS_BADGE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
            </div>
          </Link>
        ))}
      </div>
    </DashboardLayout>
  );
}
